/* ==========================================================================
   KA CLIENT CONCIERGE — shared page behaviour
   1. Marks <body> as .ka-home so the header refinements can scope to it.
   2. Toggles .ka-scrolled past 80px for the sticky header fade-in.
   3. Measures the header + sticky anchor strip and exposes them as
      --ka-header-h / --ka-anchornav-h so anchor jumps clear both.
   4. Scrollspy for the audience anchor strip.
   5. Reveals .ka-reveal elements on scroll with an 80ms sibling stagger.
   6. Draws the Example Journey timelines on scroll into view.

   Every motion behaviour is a no-op under prefers-reduced-motion.
   Guarded so that multiple <script> tags resolving to this asset only run
   the initialiser once.
   ========================================================================== */
(function () {
  if (window.__kaHomeInit) return;
  window.__kaHomeInit = true;

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    var root = document.documentElement;
    var isKaPage = !!document.querySelector('[data-ka-home]');

    /* ---- 1. Scope flag for header refinements ------------------------ */
    if (isKaPage) document.body.classList.add('ka-home');

    /* ---- 2 + 3. Sticky header, and measured offsets ------------------ */
    var header = document.querySelector('.shopify-section-header, .header-wrapper');
    var anchorNav = document.querySelector('[data-ka-anchornav]');

    function measure() {
      if (header) {
        root.style.setProperty('--ka-header-h', header.offsetHeight + 'px');
      }
      if (anchorNav) {
        root.style.setProperty('--ka-anchornav-h', anchorNav.offsetHeight + 'px');
      }
    }

    measure();
    window.addEventListener('resize', measure, { passive: true });

    if (isKaPage) {
      var ticking = false;

      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          document.body.classList.toggle('ka-scrolled', window.scrollY > 80);
          ticking = false;
        });
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    /* ---- 4. Anchor-strip scrollspy ----------------------------------- */
    if (anchorNav && 'IntersectionObserver' in window) {
      var links = Array.prototype.slice.call(
        anchorNav.querySelectorAll('a[href^="#"]')
      );

      var targets = links
        .map(function (a) {
          var el = document.getElementById(a.getAttribute('href').slice(1));
          return el ? { link: a, el: el } : null;
        })
        .filter(Boolean);

      if (targets.length) {
        var setActive = function (el) {
          targets.forEach(function (t) {
            t.link.classList.toggle('is-active', t.el === el);
            if (t.el === el) {
              t.link.setAttribute('aria-current', 'true');
            } else {
              t.link.removeAttribute('aria-current');
            }
          });
        };

        var visible = [];

        var spy = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var i = visible.indexOf(entry.target);
              if (entry.isIntersecting && i === -1) visible.push(entry.target);
              if (!entry.isIntersecting && i !== -1) visible.splice(i, 1);
            });

            if (!visible.length) return;

            /* Highest section currently in the reading band wins */
            visible.sort(function (a, b) {
              return a.getBoundingClientRect().top - b.getBoundingClientRect().top;
            });
            setActive(visible[0]);
          },
          { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
        );

        targets.forEach(function (t) {
          spy.observe(t.el);
        });
      }
    }

    /* ---- 5. Scroll reveal -------------------------------------------- */
    var targetsReveal = document.querySelectorAll('.ka-reveal');
    var timelines = document.querySelectorAll('[data-ka-timeline]');

    if (reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targetsReveal, function (el) {
        el.classList.add('is-visible');
      });
      Array.prototype.forEach.call(timelines, function (el) {
        el.classList.add('is-drawn');
      });
      return;
    }

    /* Stagger siblings by 80ms within each reveal group */
    var groups = document.querySelectorAll('[data-ka-reveal-group]');
    for (var g = 0; g < groups.length; g++) {
      var kids = groups[g].querySelectorAll(':scope > .ka-reveal');
      for (var k = 0; k < kids.length; k++) {
        kids[k].style.setProperty('--ka-reveal-delay', k * 80 + 'ms');
      }
    }

    if (targetsReveal.length) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
      );

      for (var t = 0; t < targetsReveal.length; t++) {
        observer.observe(targetsReveal[t]);
      }
    }

    /* ---- 6. Timeline draw -------------------------------------------- */
    if (timelines.length) {
      /* Opt the timeline into its hidden pre-draw state only once we know
         JS is running, so a JS failure never leaves an empty rail. */
      Array.prototype.forEach.call(timelines, function (el) {
        el.classList.add('ka-tl--anim');
      });

      var drawObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-drawn');
            drawObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -15% 0px', threshold: 0.2 }
      );

      Array.prototype.forEach.call(timelines, function (el) {
        drawObserver.observe(el);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
